import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Card, message, Spin, Result, Divider } from 'antd';
import { WechatOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { parseQueryString } from '../utils/helpers';
import { fetchOrderInfo, requestWechatPayment, queryPaymentResult } from '../services/api';

// Helper function to call WeChat JSSDK payment
const callWechatPay = (wxPayParams) => {
  return new Promise((resolve, reject) => {
    // Ensure WechatJSBridge is available
    if (typeof window.WeixinJSBridge === 'undefined') {
      if (document.addEventListener) {
        document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
      } else if (document.attachEvent) {
        document.attachEvent('WeixinJSBridgeReady', onBridgeReady);
        document.attachEvent('onWeixinJSBridgeReady', onBridgeReady);
      }
    } else {
      onBridgeReady();
    }

    // Function to invoke WeChat payment
    function onBridgeReady() {
      window.WeixinJSBridge.invoke(
        'getBrandWCPayRequest',
        {
          appId: wxPayParams.appId,
          timeStamp: wxPayParams.timeStamp,
          nonceStr: wxPayParams.nonceStr,
          package: wxPayParams.package,
          signType: wxPayParams.signType,
          paySign: wxPayParams.paySign
        },
        function(res) {
          if (res.err_msg === 'get_brand_wcpay_request:ok') {
            // Payment successful
            resolve({ success: true });
          } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
            // User canceled payment
            resolve({ success: false, canceled: true });
          } else {
            // Payment failed
            reject(new Error(res.err_msg || 'WeChat payment failed'));
          }
        }
      );
    }
  });
};

const OrderPaymentPage = () => {
  const [orderId, setOrderId] = useState('');
  const [shortOrderId, setShortOrderId] = useState('');
  const [orderInfo, setOrderInfo] = useState(null);
  const [isOnlyChargeOrder, setIsOnlyChargeOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRechargeAmount, setSelectedRechargeAmount] = useState(39.9); // 默认选中最高金额
  const [selectedRechargeType, setSelectedRechargeType] = useState(4);
  const navigate = useNavigate();
  const location = useLocation();

  // 充值金额选项 - 从高到低排列
  const rechargeOptions = [
    { 
      value: 39.9, 
      label: '39.9元', 
      coins: 4300, 
      bonus: '300能量币',
      description: '赠送300能量币(限时优惠)',
      type: 4, 
      isRecommended: true
    },
    { 
      value: 19.9, 
      label: '19.9元', 
      coins: 2000, 
      bonus: '',
      type: 2, 
      description: '基础充值'
    },
    { 
      value: 9.9, 
      label: '9.9元', 
      coins: 1000, 
      bonus: '',
      description: '基础充值',
      type: 1,
    },
  ];

  useEffect(() => {
    // Parse order ID from URL query parameters
    const queryParams = parseQueryString(location.search);
    const id = queryParams.orderId;
    
    if (!id) {
      setError('订单号不能为空');
      setInitialLoading(false);
      return;
    }
    
    setOrderId(id);
    setShortOrderId(id.slice(-12).toUpperCase());
    
    // Fetch order information
    fetchOrderDetails(id);
  }, [location.search]);

  const fetchOrderDetails = async (id) => {
    try {
      setInitialLoading(true);
      const response = await fetchOrderInfo(id);
      
      if (response.success) {
        // 查看订单是否已支付
        let data = response.data;
        if(data.status === 'completed') {
          message.success('订单已支付');
          let shortOrderId = data.orderId.slice(-12).toUpperCase();
          let paySuccURL = `/success?amount=${data.amount}&orderId=${shortOrderId}&paidAt=${encodeURIComponent(data.payTIme || new Date().toISOString())}`;
          console.log("windows location ", paySuccURL)
          navigate(paySuccURL)
        }
        setOrderInfo(response.data);
        
        const isChargeOrder = typeof id === 'string' && id.toLowerCase().startsWith('charge');
        if (!isChargeOrder) {
          if (!response.data.amount || response.data.amount <= 0) {
            setError('订单金额无效');
          }
        } else {
          setIsOnlyChargeOrder(true);
        }
      } else {
        setError('获取订单信息失败');
      }
    } catch (error) {
      console.error('获取订单信息出错:', error);
      setError('获取订单信息出错');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleUnifiedPayment = async () => {
    if (selectedRechargeAmount) {
      // 充值模式：充值金额并解锁帖子
      if (selectedRechargeAmount <= 0) {
        message.error('请选择充值金额');
        return;
      }

      setLoading(true);

      
      // 订单和充值 支付模型一致
      if (!orderInfo) {
        message.error('订单金额无效');
        return;
      }

      setLoading(true);

      try {
        // Request WeChat payment for order only
        const paymentResponse = await requestWechatPayment({
          orderId: orderId,
          chargeType: selectedRechargeType || 0,
        });
        
        if (paymentResponse.success && paymentResponse.data) {
          message.info(selectedRechargeAmount ? '正在调用微信支付进行充值...' : '正在调用微信支付...');
          
          try {
            // Call WeChat JSSDK to initiate payment
            const payResult = await callWechatPay(paymentResponse.data);
            
            if (payResult.success) {
              await checkUnifiedPaymentStatus(orderId, selectedRechargeAmount ? 'recharge' : 'order');
            } else if (payResult.canceled) {
              message.info('支付已取消');
              setLoading(false);
            } else {
              message.error('支付失败');
              setLoading(false);
            }
          } catch (error) {
            console.error('微信支付调用失败:', error);
            message.error('微信支付调用失败: ' + (error.message || '未知错误'));
            setLoading(false);
          }
        } else {
          console.error('请求支付出错:', paymentResponse.error);
          message.error(paymentResponse.error || '创建支付请求失败');
          setLoading(false);
        }
      } catch (error) {
        console.error('请求支付出错:', error);
        message.error('请求支付出错: ' + (error.message || '未知错误'));
        setLoading(false);
      }
    }
  };

  const checkUnifiedPaymentStatus = async (paymentOrderId, paymentType) => {
    try {
      const statusResponse = await queryPaymentResult(paymentOrderId);
      
      if (statusResponse.success) {
        const { paymentStatus, payTime } = statusResponse.data;
        
        if (paymentStatus === 'success' || statusResponse.data.status === 'completed') {
          if (paymentType === 'recharge') {
            message.success(isOnlyChargeOrder ? '充值成功' : '充值成功，帖子已解锁');
            // Navigate to success page with recharge info
            let toSuccURL = `/success?amount=${selectedRechargeAmount}&orderId=${paymentOrderId}&paidAt=${encodeURIComponent(payTime || new Date().toISOString())}&type=recharge&unlocked=${orderId}`;
            navigate(toSuccURL);
          } else {
            message.success('支付成功');
            // Navigate to success page with order info
            let toSuccURL = `/success?amount=${orderInfo.amount}&orderId=${orderId}&paidAt=${encodeURIComponent(payTime || new Date().toISOString())}`;
            navigate(toSuccURL);
          }
        } else if (paymentStatus === 'processing' || statusResponse.data.status === 'pending') {
          message.info('支付处理中，请稍后...');
          // Check again after a delay
          setTimeout(() => checkUnifiedPaymentStatus(paymentOrderId, paymentType), 1500);
        } else {
          message.error('支付失败');
          setLoading(false);
        }
      } else {
        message.error(statusResponse.error || '查询支付状态失败');
        setLoading(false);
      }
    } catch (error) {
      console.error('查询支付状态出错:', error);
      message.error('查询支付状态出错: ' + (error.message || '未知错误'));
      setLoading(false);
    }
  };

  // 计算显示的支付信息
  const getPaymentInfo = () => {
    if (selectedRechargeAmount) {
      const selectedOption = rechargeOptions.find(opt => opt.value === selectedRechargeAmount);
      return {
        amount: selectedRechargeAmount,
        description: isOnlyChargeOrder ? `充值 ${selectedOption?.coins} 能量币` : `充值 ${selectedOption?.coins} 能量币并解锁帖子`,
        buttonText: isOnlyChargeOrder ? '充值' : '充值并解锁',
        type: 'recharge'
      };
    } else {
      return {
        amount: orderInfo?.amount || 0,
        description: isOnlyChargeOrder ? '充值' : '充值并解锁此帖子',
        buttonText: isOnlyChargeOrder ? '充值' : '支付解锁',
        type: 'order'
      };
    }
  };

  const handleRechargeSelect = (amount, type) => {
    setSelectedRechargeAmount(amount);
    setSelectedRechargeType(type);
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载订单信息..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-page">
        <Result
          status="error"
          title="获取订单失败"
          subTitle={error}
        />
      </div>
    );
  }

  return (
    <div className="payment-page" style={{ 
      padding: '20px 16px', 
      maxWidth: '400px', 
      margin: '0 auto', 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* 页面标题 */}
      <div className="payment-header" style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        paddingTop: '20px',
        display: 'none',
      }}>
        <h1 style={{ 
          fontSize: '18px', 
          margin: '0 0 8px 0', 
          color: '#333',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span>🔒</span> 直接解锁
        </h1>
        {/* 订单编号显示 */}
        <p style={{ 
          margin: '0', 
          fontSize: '13px', 
          color: '#8e8e93'
        }}>
          订单编号: {shortOrderId}
        </p>
      </div>

      {/* 直接解锁选项 */}
      <div 
        className={`payment-option ${!selectedRechargeAmount ? 'selected' : ''}`}
        onClick={() => setSelectedRechargeAmount(null)}
        style={{
          backgroundColor: '#fff',
          border: !selectedRechargeAmount ? '2px solid #007AFF' : '1px solid #e1e5e9',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          cursor: 'pointer',
          boxShadow: !selectedRechargeAmount ? '0 2px 12px rgba(0, 122, 255, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          display: "none",
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '500', 
              color: '#333',
              marginBottom: '4px'
            }}>
              付费解锁全文
            </div>
            <div style={{ 
              fontSize: '13px', 
              color: '#8e8e93'
            }}>
              仅解锁当前帖子
            </div>
          </div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#333'
          }}>
            ¥{Number(orderInfo?.amount || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* 充值余额标题 */}
      <div style={{ 
        fontSize: '16px', 
        fontWeight: '500', 
        color: '#666',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ 
          width: '4px', 
          height: '16px', 
          backgroundColor: '#007AFF', 
          borderRadius: '2px' 
        }}></span>
        {isOnlyChargeOrder ? '充值' : '充值并解锁当前帖子'}
      </div>

      {/* 充值选项列表 */}
      <div style={{ marginBottom: '24px' }}>
        {rechargeOptions.map((option, index) => (
          <div 
            key={option.value}
            className={`recharge-option ${selectedRechargeAmount === option.value ? 'selected' : ''}`}
            onClick={() => handleRechargeSelect(option.value, option.type)}
            style={{
              backgroundColor: '#fff',
              border: selectedRechargeAmount === option.value ? '2px solid #007AFF' : '1px solid #e1e5e9',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: index === rechargeOptions.length - 1 ? '0' : '12px',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: selectedRechargeAmount === option.value ? '0 2px 12px rgba(0, 122, 255, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease'
            }}
          >
            {option.isRecommended && (
              <div style={{
                position: 'absolute',
                top: '-6px',
                left: '12px',
                backgroundColor: '#ff3b30',
                color: 'white',
                padding: '3px 8px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                zIndex: 1
              }}>
                推荐
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, paddingRight: '12px' }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500', 
                  color: '#333',
                  marginBottom: '4px'
                }}>
                  充值{option.label}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#333',
                  marginBottom: option.bonus ? '4px' : '0'
                }}>
                  获得 {option.coins} 能量币
                </div>
                {option.bonus && (
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#ff9500',
                    fontWeight: '500'
                  }}>
                    赠送{option.bonus}(限时优惠)
                  </div>
                )}
              </div>
              
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#333',
                textAlign: 'right'
              }}>
                ¥{option.value.toFixed(1)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 统一支付按钮 */}
      <div style={{ 
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        padding: '16px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e1e5e9'
      }}>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <Button
            type="primary"
            size="large"
            block
            onClick={handleUnifiedPayment}
            loading={loading}
            disabled={loading}
            style={{
              backgroundColor: '#8e7cc3',
              borderColor: '#8e7cc3',
              borderRadius: '24px',
              height: '48px',
              fontSize: '16px',
              fontWeight: '500',
              boxShadow: 'none',
              marginBottom: '12px'
            }}
          >
            {loading ? '处理中...' : `确认${getPaymentInfo().buttonText}`}
          </Button>
          
          <div style={{ 
            textAlign: 'center',
            fontSize: '13px',
            color: '#8e8e93',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}>
            <span style={{ 
              width: '16px', 
              height: '16px', 
              borderRadius: '50%', 
              border: '1px solid #8e8e93',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px'
            }}>i</span>
            {isOnlyChargeOrder ? '充值后请刷新网页' : selectedRechargeAmount ? '支付完成后将立即充值并解锁帖子' : '支付完成后将解锁当前帖子'}
          </div>
        </div>
      </div>

      {/* 底部占位，避免内容被固定按钮遮挡 */}
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

export default OrderPaymentPage;
